import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { fromZonedTime, toZonedTime } from "date-fns-tz";
import { Fire, Phone, Timer, TrendUp, MagnifyingGlass } from "@phosphor-icons/react";
import { RepCard } from "../shared/RepCard";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { api, formatApiErrorDetail } from "../../lib/api";
import { usePolling } from "../../hooks/usePolling";

const DEAL_STAGES = ["Pipeline", "Submitted", "Underwriting", "Approved", "Funded", "Declined"];
const STAGE_COLORS = {
  Pipeline: "border-igc-gold/20 text-igc-muted",
  Submitted: "border-igc-gold/30 text-igc-goldText/80",
  Underwriting: "border-amber-600/40 text-amber-600",
  Approved: "border-igc-gold/40 text-igc-goldText",
  Funded: "border-igc-success/50 text-igc-success",
  Declined: "border-igc-danger/40 text-igc-danger",
};

function getNextWednesday5pmET() {
  const nowET = toZonedTime(new Date(), "America/New_York");
  const target = new Date(nowET);
  target.setHours(12, 0, 0, 0);
  const dayDiff = (3 - target.getDay() + 7) % 7;
  target.setDate(target.getDate() + dayDiff);
  if (dayDiff === 0 && nowET.getTime() > target.getTime()) {
    target.setDate(target.getDate() + 7);
  }
  return fromZonedTime(target, "America/New_York");
}

function useCutoffCountdown() {
  const [remaining, setRemaining] = useState("");
  useEffect(() => {
    const tick = () => {
      const diffMs = getNextWednesday5pmET().getTime() - Date.now();
      const d = Math.floor(diffMs / 86400000);
      const h = Math.floor((diffMs % 86400000) / 3600000);
      const m = Math.floor((diffMs % 3600000) / 60000);
      const s = Math.floor((diffMs % 60000) / 1000);
      setRemaining(`${d}d ${h}h ${m}m ${s}s`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);
  return remaining;
}

export function ManagerCommandCenterTab() {
  const navigate = useNavigate();
  const [reps, setReps] = useState([]);
  const [center, setCenter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [messageRep, setMessageRep] = useState(null);
  const [messageText, setMessageText] = useState("");
  const [sending, setSending] = useState(false);
  const [search, setSearch] = useState("");
  const cutoff = useCutoffCountdown();
  const visibleReps = reps.filter((r) => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (r.display_name || "").toLowerCase().includes(q) || (r.user_id || "").toLowerCase().includes(q);
  });

  const loadCenter = () => {
    Promise.all([api.get("/manager/reps/cards"), api.get("/manager/command-center")])
      .then(([repsRes, centerRes]) => {
        setReps(repsRes.data);
        setCenter(centerRes.data);
      })
      .catch(() => {});
  };

  useEffect(() => {
    Promise.all([api.get("/manager/reps/cards"), api.get("/manager/command-center")])
      .then(([repsRes, centerRes]) => {
        setReps(repsRes.data);
        setCenter(centerRes.data);
      })
      .catch(() => toast.error("Couldn't load your Command Center. Please refresh."))
      .finally(() => setLoading(false));
  }, []);

  usePolling(loadCenter, 15000);

  const dealTotals = useMemo(() => {
    if (!center) return { count: 0 };
    return Object.values(center.deal_pipeline).reduce((acc, s) => ({ count: acc.count + s.count }), { count: 0 });
  }, [center]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!messageText.trim() || sending) return;
    setSending(true);
    try {
      await api.post(`/manager/reps/${messageRep.id}/message`, { message: messageText });
      toast.success(`Sent to ${messageRep.display_name}`);
      setMessageRep(null);
      setMessageText("");
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  if (loading) return <p className="text-igc-muted text-sm" data-testid="manager-command-center-loading">Loading your team's command center...</p>;
  if (!center) return null;

  return (
    <div data-testid="manager-command-center-tab" className="space-y-8">
      <section data-testid="manager-command-center-rep-board">
        <h2 className="text-igc-goldText text-xs uppercase tracking-widest font-medium mb-3">Your Team</h2>
        {reps.length === 0 ? (
          <p className="text-igc-muted text-sm">No reps on your team yet.</p>
        ) : (
          <>
            <div className="relative mb-3 max-w-sm">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-igc-muted" />
              <Input
                data-testid="manager-command-center-search-input"
                placeholder="Search by name or rep code..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                title="Search narrows the rep cards below in real time as you type — matches name or rep code."
                className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream pl-8"
              />
            </div>
            <div className="flex gap-4 overflow-x-auto pb-2" data-hscroll>
              {visibleReps.map((r) => (
                <RepCard
                  key={r.id}
                  rep={r}
                  onOpen={(rep) => navigate(`/manager/reps/${rep.id}`)}
                  onMessage={(rep) => setMessageRep(rep)}
                />
              ))}
            </div>
          </>
        )}
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <section className="bg-igc-surface border border-igc-gold/15 p-5" data-testid="manager-command-center-commission">
          <h2 className="text-igc-goldText text-xs uppercase tracking-widest font-medium mb-4 flex items-center gap-2"><Timer size={14} weight="duotone" /> Team Commission</h2>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div data-testid="manager-command-center-commission-pending">
              <p className="font-serif text-2xl text-amber-600">${center.commission.total_pending.toLocaleString()}</p>
              <p className="text-igc-muted text-[11px] uppercase tracking-wide">Pending</p>
            </div>
            <div data-testid="manager-command-center-commission-paid">
              <p className="font-serif text-2xl text-igc-success">${center.commission.total_paid.toLocaleString()}</p>
              <p className="text-igc-muted text-[11px] uppercase tracking-wide">Paid</p>
            </div>
          </div>
          <div className="border-t border-igc-gold/10 pt-3">
            <p className="text-igc-muted text-[11px] uppercase tracking-wide mb-1">Next Payout Cutoff (Wed 12PM ET)</p>
            <p className="font-serif text-lg text-igc-cream" data-testid="manager-command-center-cutoff-timer">{cutoff}</p>
          </div>
        </section>

        <section className="bg-igc-surface border border-igc-gold/15 p-5 lg:col-span-2" data-testid="manager-command-center-deal-pipeline">
          <h2 className="text-igc-goldText text-xs uppercase tracking-widest font-medium mb-4 flex items-center gap-2"><TrendUp size={14} weight="duotone" /> Team Deal Pipeline ({dealTotals.count} active)</h2>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
            {DEAL_STAGES.map((stage) => (
              <div key={stage} className={`border p-3 text-center ${STAGE_COLORS[stage]}`} data-testid={`manager-command-center-stage-${stage.toLowerCase()}`} title={`Number and total dollar value of your team's deals currently at the ${stage} stage.`}>
                <p className="font-serif text-xl">{center.deal_pipeline[stage]?.count ?? 0}</p>
                <p className="text-[10px] uppercase tracking-wide mt-1">{stage}</p>
                <p className="text-[10px] mt-1 opacity-70">${(center.deal_pipeline[stage]?.value ?? 0).toLocaleString()}</p>
              </div>
            ))}
          </div>
        </section>
      </div>

      <section data-testid="manager-command-center-hot-leads">
        <h2 className="text-igc-goldText text-xs uppercase tracking-widest font-medium mb-3 flex items-center gap-2"><Fire size={14} weight="fill" className="text-igc-danger" /> Team Hot Lead Feed</h2>
        {center.hot_leads.length === 0 ? (
          <p className="text-igc-muted text-sm">No high-intent leads flagged on your team right now.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {center.hot_leads.map((l) => (
              <div key={l.id} className="bg-igc-surface border border-igc-danger/30 p-4 relative" data-testid={`manager-command-center-hot-lead-${l.id}`} title="A high-intent lead on your team, likely ready to close soon.">
                <p className="text-igc-cream text-sm font-medium">{l.name}</p>
                <p className="text-igc-muted text-xs mb-2 capitalize">{l.source} — intent {l.intent_score}</p>
                {l.phone && (
                  <a href={`tel:${l.phone}`} title="Dials this lead's phone number directly." className="inline-flex items-center gap-1.5 bg-igc-gold hover:bg-igc-goldHover text-black text-xs px-3 py-1.5 rounded-full">
                    <Phone size={12} weight="fill" /> Call
                  </a>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      <Dialog open={!!messageRep} onOpenChange={(v) => !v && setMessageRep(null)}>
        <DialogContent className="bg-igc-surface border-igc-gold/20 text-igc-cream" data-testid="manager-message-rep-modal">
          <DialogHeader><DialogTitle className="font-serif">Message {messageRep?.display_name}</DialogTitle></DialogHeader>
          <form onSubmit={sendMessage} className="space-y-3">
            <Textarea
              data-testid="manager-message-rep-textarea"
              required
              placeholder="Type your message to this rep..."
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              title="Your message is delivered directly to this rep through Nia."
              className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream min-h-28"
            />
            <Button data-testid="manager-message-rep-submit-button" type="submit" disabled={sending} title="Sends this message to the rep." className="w-full bg-igc-gold hover:bg-igc-goldHover text-black">
              {sending ? "Sending..." : "Send"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
